import { Node, mergeAttributes } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { PluginKey } from '@tiptap/pm/state'
import { Suggestion, SuggestionOptions } from '@tiptap/suggestion'

import { Member } from '@tribeplatform/gql-client/types'

export type MentionOptions = {
  mentions: Partial<Member>[]
  HTMLAttributes: Record<string, unknown>
  renderLabel: (props: {
    options: MentionOptions
    node: ProseMirrorNode
  }) => string
  suggestion: Omit<SuggestionOptions, 'editor'>
}

export const MentionPluginKey = new PluginKey('mention')

export const Mention = Node.create<MentionOptions>({
  name: 'mention',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel({ node }) {
        return `${node.attrs.label ?? node.attrs.id}`
      },
      mentions: [],
      suggestion: {
        char: '@',
        pluginKey: MentionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const { nodeAfter } = editor.view.state.selection.$to
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()
        },
        allow: ({ editor, range }) => {
          try {
            const $from = editor.state.doc.resolve(range.from)
            const type = editor.schema.nodes[this.name]
            const allow = !!$from.parent.type.contentMatch.matchType(type)
            return allow
          } catch (e) {
            if (e instanceof RangeError) {
              /**
               * Probably pasted content, ending with '@' character which causes the resolve to fail.
               * By not allowing the node it's rendered as plain text.
               */
              return false
            }
            throw e
          }
        },
      },
    }
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        rendered: false,
      },

      label: {
        default: null,
        rendered: false,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `a[data-type="${this.name}"]`,
        getAttrs: dom => {
          if (typeof dom === 'string') {
            return {}
          }
          const element = dom as HTMLImageElement
          const id = element.getAttribute('data-id')
          const innerText = element.innerText.replace(
            this.options.suggestion.char,
            '',
          )
          const mention = this.options.mentions?.find(m => m.id === id)

          return {
            id,
            label: mention?.name || innerText,
          }
        },
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(
        { 'data-type': this.name },
        { 'data-id': node.attrs.id },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ]
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    })
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize,
              )

              return false
            }

            return undefined
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
